import { z } from 'zod';

import type {
  AssignShipmentDriverRequest,
  AssignShipmentDriverResponse,
  CorrectShipmentWeighEventRequest,
  CorrectShipmentWeighEventResponse,
  CreateDriverShipmentWeighEventRequest,
  CreateDriverShipmentWeighEventResponse,
  CreateShipmentQuickCaptureEventRequest,
  CreateShipmentQuickCaptureEventResponse,
  CreateShipmentWeighEventRequest,
  CreateShipmentWeighEventResponse,
  DriverShipmentOfficialSettlementSummaryDto,
  DriverShipmentWeighCaptureItemDto,
  DriverShipmentWeighComparisonSummaryDto,
  DriverShipmentWeighEventSummaryDto,
  DriverShipmentWeighEvidenceSummaryDto,
  DriverShipmentWeighNetFeedbackDto,
  GetAssignedShipmentQuickCaptureAvailabilityRequest,
  GetAssignedShipmentQuickCaptureAvailabilityResponse,
  GetDriverShipmentWeighCaptureRequest,
  GetDriverShipmentWeighCaptureResponse,
  GetShipmentQuickCaptureTargetRequest,
  GetShipmentQuickCaptureTargetResponse,
  LinkShipmentWeighEventsRequest,
  LinkShipmentWeighEventsResponse,
  ListDriverShipmentWeighCaptureRequest,
  ListDriverShipmentWeighCaptureResponse,
  ListShipmentQuickCaptureTargetsRequest,
  ListShipmentQuickCaptureTargetsResponse,
  ListShipmentDriverAssignmentsRequest,
  ListShipmentDriverAssignmentsResponse,
  ListShipmentWeighEvidenceRequest,
  ListShipmentWeighEvidenceResponse,
  RemoveShipmentDriverAssignmentRequest,
  RemoveShipmentDriverAssignmentResponse,
  RecoverShipmentQuickCaptureEventRequest,
  RecoverShipmentQuickCaptureEventResponse,
  ShipmentDriverAssignmentDto,
  ShipmentDriverAssignmentEmployeeOptionDto,
  ShipmentMaterialRefDto,
  ShipmentQuickCaptureMaterialTaskMetadataDto,
  ShipmentQuickCaptureReadinessSummaryDto,
  ShipmentWeighEventRecoveryDto,
  ShipmentWeighEventDto,
  ShipmentWeighEvidenceComparisonDto,
  ShipmentWeighNetCandidateDto,
  ShipmentWeighOperationalCardDto,
  ShipmentWeighPairSuggestionDto,
  UnlinkShipmentWeighEventRequest,
  UnlinkShipmentWeighEventResponse,
  VerifyShipmentWeighEventRequest,
  VerifyShipmentWeighEventResponse,
  VoidShipmentWeighEventRequest,
  VoidShipmentWeighEventResponse,
} from './shipment-weigh-evidence.dto.js';
import {
  IsoDateTimeSchema,
  ShipmentDriverAssignmentStatusSchema,
  ShipmentMaterialRefTypeSchema,
  ShipmentQuickCaptureCannotSubmitReasonSchema,
  ShipmentQuickCaptureCapturePhaseSchema,
  ShipmentQuickCaptureMaterialTaskStatusSchema,
  ShipmentQuickCaptureReadinessStatusSchema,
  ShipmentQuickCaptureTargetKindSchema,
  ShipmentWeighEntryStatusSchema,
  ShipmentWeighCaptureSourceSchema,
  ShipmentWeighInventoryComparisonStatusSchema,
  ShipmentWeighNetCandidateSourceSchema,
  ShipmentWeighNetFeedbackStatusSchema,
  ShipmentWeighOperationalCardSourceSchema,
  ShipmentWeighPaymentThresholdStatusSchema,
  ShipmentWeighReviewStatusSchema,
  ShipmentWeighScaleRoleSchema,
  ShipmentWeighTypeSchema,
} from './shipments-types.js';
import { OutboundRecordTypeSchema } from '../outbound/outbound-record-reference.schema.js';

const optionalNullablePositiveNumberSchema = z.number().gt(0).nullable().optional();
const nullableNonNegativeNumberSchema = z.number().min(0).nullable();
const positiveNumberSchema = z.number().gt(0);
const trimmedStringSchema = z.string().trim().min(1);
const noteSchema = trimmedStringSchema.nullable().optional();

export const ShipmentWeighEventRecoveryActionSchema = z.enum(['WITHDRAW', 'REPLACE']);

const outboundWeighEvidenceReferenceSchema = {
  shipmentId: z.uuid(),
  outboundRecordType: OutboundRecordTypeSchema.optional(),
  outboundRecordId: z.uuid().optional(),
};

function validateOutboundWeighEvidenceReference(value: unknown, ctx: z.RefinementCtx): void {
  const reference = value as {
    shipmentId?: string;
    outboundRecordType?: string;
    outboundRecordId?: string;
  };
  const hasType = reference.outboundRecordType !== undefined;
  const hasId = reference.outboundRecordId !== undefined;
  if (hasType !== hasId) {
    ctx.addIssue({
      code: 'custom',
      message: 'outboundRecordType and outboundRecordId must be provided together.',
      path: [hasType ? 'outboundRecordId' : 'outboundRecordType'],
    });
  }
}

export const ShipmentMaterialRefSchema: z.ZodType<ShipmentMaterialRefDto> = z
  .object({
    shipmentMaterialRefType: ShipmentMaterialRefTypeSchema,
    shipmentMaterialRefId: z.uuid(),
    materialId: z.uuid(),
  })
  .strict();

export const ShipmentWeighEventSchema: z.ZodType<ShipmentWeighEventDto> = z
  .object({
    weighEventId: z.uuid(),
    companyId: z.uuid(),
    shipmentId: z.uuid(),
    outboundRecordType: OutboundRecordTypeSchema,
    outboundRecordId: z.uuid(),
    stationId: z.uuid(),
    shipmentMaterialRef: ShipmentMaterialRefSchema,
    scaleRole: ShipmentWeighScaleRoleSchema,
    scaleLabel: trimmedStringSchema.nullable(),
    weighType: ShipmentWeighTypeSchema,
    weightLb: nullableNonNegativeNumberSchema,
    grossWeightLb: nullableNonNegativeNumberSchema,
    tareWeightLb: nullableNonNegativeNumberSchema,
    correctedWeightLb: nullableNonNegativeNumberSchema,
    correctedGrossWeightLb: nullableNonNegativeNumberSchema,
    correctedTareWeightLb: nullableNonNegativeNumberSchema,
    effectiveWeightLb: nullableNonNegativeNumberSchema,
    effectiveGrossWeightLb: nullableNonNegativeNumberSchema,
    effectiveTareWeightLb: nullableNonNegativeNumberSchema,
    effectiveNetWeightLb: nullableNonNegativeNumberSchema,
    pairedGrossEventId: z.uuid().nullable(),
    pairedAt: IsoDateTimeSchema.nullable(),
    pairedByUserId: z.uuid().nullable(),
    pairingNote: trimmedStringSchema.nullable(),
    photoUrl: trimmedStringSchema.nullable(),
    notes: trimmedStringSchema.nullable(),
    captureSource: ShipmentWeighCaptureSourceSchema,
    capturedByUserId: z.uuid().nullable(),
    capturedAt: IsoDateTimeSchema,
    reviewStatus: ShipmentWeighReviewStatusSchema,
    correctedByUserId: z.uuid().nullable(),
    correctedAt: IsoDateTimeSchema.nullable(),
    correctionReason: trimmedStringSchema.nullable(),
    verifiedByUserId: z.uuid().nullable(),
    verifiedAt: IsoDateTimeSchema.nullable(),
    voidedByUserId: z.uuid().nullable(),
    voidedAt: IsoDateTimeSchema.nullable(),
    voidReason: trimmedStringSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const ShipmentWeighNetCandidateSchema: z.ZodType<ShipmentWeighNetCandidateDto> = z
  .object({
    candidateId: trimmedStringSchema,
    source: ShipmentWeighNetCandidateSourceSchema,
    sourceEventIds: z.array(z.uuid()).nonempty(),
    grossEventId: z.uuid().nullable(),
    tareEventId: z.uuid().nullable(),
    netEventId: z.uuid().nullable(),
    grossWeightLb: nullableNonNegativeNumberSchema,
    tareWeightLb: nullableNonNegativeNumberSchema,
    netWeightLb: positiveNumberSchema.or(z.literal(0)),
    shipmentMaterialRef: ShipmentMaterialRefSchema,
    scaleRole: ShipmentWeighScaleRoleSchema,
    scaleLabel: trimmedStringSchema.nullable(),
    reviewStatus: ShipmentWeighReviewStatusSchema,
    capturedAt: IsoDateTimeSchema,
    inventoryComparisonStatus: ShipmentWeighInventoryComparisonStatusSchema,
    paymentThresholdStatus: ShipmentWeighPaymentThresholdStatusSchema,
  })
  .strict();

export const ShipmentWeighPairSuggestionSchema: z.ZodType<ShipmentWeighPairSuggestionDto> = z
  .object({
    suggestionId: trimmedStringSchema,
    tareEventId: z.uuid(),
    grossEventId: z.uuid(),
    grossWeightLb: positiveNumberSchema,
    tareWeightLb: positiveNumberSchema,
    netWeightLb: positiveNumberSchema.or(z.literal(0)),
    shipmentMaterialRef: ShipmentMaterialRefSchema,
    scaleRole: ShipmentWeighScaleRoleSchema,
    scaleLabel: trimmedStringSchema.nullable(),
    capturedAt: IsoDateTimeSchema,
    requiresNote: z.boolean(),
    noteReason: z.enum(['mixed_role', 'mixed_label', 'mixed_role_and_label']).nullable(),
    inventoryComparisonStatus: ShipmentWeighInventoryComparisonStatusSchema,
    paymentThresholdStatus: ShipmentWeighPaymentThresholdStatusSchema,
  })
  .strict();

export const ShipmentWeighOperationalCardSchema: z.ZodType<ShipmentWeighOperationalCardDto> = z
  .object({
    cardId: trimmedStringSchema,
    source: ShipmentWeighOperationalCardSourceSchema,
    sourceEventIds: z.array(z.uuid()).nonempty(),
    grossEventId: z.uuid().nullable(),
    tareEventId: z.uuid().nullable(),
    netEventId: z.uuid().nullable(),
    completionEventId: z.uuid().nullable(),
    completionWeighType: ShipmentWeighTypeSchema.nullable(),
    scaleRole: ShipmentWeighScaleRoleSchema,
    scaleLabel: trimmedStringSchema.nullable(),
    grossWeightLb: nullableNonNegativeNumberSchema,
    tareWeightLb: nullableNonNegativeNumberSchema,
    netWeightLb: nullableNonNegativeNumberSchema,
    netLabel: trimmedStringSchema.nullable(),
    entryStatus: ShipmentWeighEntryStatusSchema,
    entryStatusLabel: trimmedStringSchema,
    reviewStatus: ShipmentWeighReviewStatusSchema,
    stateLabel: trimmedStringSchema,
    capturedAt: IsoDateTimeSchema,
    inventoryComparisonStatus: ShipmentWeighInventoryComparisonStatusSchema,
    inventoryLabel: trimmedStringSchema,
    paymentThresholdStatus: ShipmentWeighPaymentThresholdStatusSchema,
    paymentThresholdLabel: trimmedStringSchema,
    hasPhoto: z.boolean(),
    hasNotes: z.boolean(),
  })
  .strict();

export const ShipmentWeighEvidenceComparisonSchema: z.ZodType<ShipmentWeighEvidenceComparisonDto> =
  z
    .object({
      inventoryComparisonStatus: ShipmentWeighInventoryComparisonStatusSchema,
      paymentThresholdStatus: ShipmentWeighPaymentThresholdStatusSchema,
      selectedCandidateId: trimmedStringSchema.nullable(),
      processorNetWeightLb: nullableNonNegativeNumberSchema,
      huExpectedPhysicalWeightLb: nullableNonNegativeNumberSchema,
      inventoryDifferenceLb: z.number().nullable(),
      reportableWeightLb: nullableNonNegativeNumberSchema,
      minimumPaymentThresholdLb: nullableNonNegativeNumberSchema,
      paymentThresholdDifferenceLb: z.number().nullable(),
    })
    .strict();

export const DriverShipmentWeighEvidenceSummarySchema: z.ZodType<DriverShipmentWeighEvidenceSummaryDto> =
  z
    .object({
      eventCount: z.number().int().min(0),
      latestCapturedAt: IsoDateTimeSchema.nullable(),
      latestWeighType: ShipmentWeighTypeSchema.nullable(),
      latestNetWeightLb: nullableNonNegativeNumberSchema,
    })
    .strict();

export const DriverShipmentWeighComparisonSummarySchema: z.ZodType<DriverShipmentWeighComparisonSummaryDto> =
  z
    .object({
      inventoryComparisonStatus: ShipmentWeighInventoryComparisonStatusSchema,
      inventoryLabel: trimmedStringSchema,
      paymentThresholdStatus: ShipmentWeighPaymentThresholdStatusSchema,
      paymentThresholdLabel: trimmedStringSchema,
      processorNetWeightLb: nullableNonNegativeNumberSchema,
    })
    .strict();

export const DriverShipmentWeighNetFeedbackSchema: z.ZodType<DriverShipmentWeighNetFeedbackDto> = z
  .object({
    status: ShipmentWeighNetFeedbackStatusSchema,
    label: trimmedStringSchema,
    netLabel: trimmedStringSchema.nullable(),
    netWeightLb: nullableNonNegativeNumberSchema,
    grossWeightLb: nullableNonNegativeNumberSchema,
    tareWeightLb: nullableNonNegativeNumberSchema,
    expectedLoadWeightLb: nullableNonNegativeNumberSchema,
    differenceLb: z.number().nullable(),
    inventoryComparisonStatus: ShipmentWeighInventoryComparisonStatusSchema,
    inventoryLabel: trimmedStringSchema,
    paymentThresholdStatus: ShipmentWeighPaymentThresholdStatusSchema,
    paymentThresholdLabel: trimmedStringSchema,
  })
  .strict();

export const DriverShipmentOfficialSettlementSummarySchema: z.ZodType<DriverShipmentOfficialSettlementSummaryDto> =
  z
    .object({
      sourceLabel: z.literal('Official settlement from InboundReceipt'),
      shippingReportNumber: trimmedStringSchema.nullable(),
      receivedWeightLb: nullableNonNegativeNumberSchema,
    })
    .strict();

export const ShipmentWeighEventRecoverySchema: z.ZodType<ShipmentWeighEventRecoveryDto> = z
  .object({
    canWithdraw: z.boolean(),
    canReplace: z.boolean(),
    expiresAt: IsoDateTimeSchema,
    token: trimmedStringSchema.nullable().optional(),
  })
  .strict();

export const DriverShipmentWeighEventSummarySchema: z.ZodType<DriverShipmentWeighEventSummaryDto> =
  z
    .object({
      weighEventId: z.uuid(),
      weighType: ShipmentWeighTypeSchema,
      scaleRole: ShipmentWeighScaleRoleSchema,
      scaleLabel: trimmedStringSchema.nullable(),
      weightLb: nullableNonNegativeNumberSchema,
      grossWeightLb: nullableNonNegativeNumberSchema,
      tareWeightLb: nullableNonNegativeNumberSchema,
      netWeightLb: nullableNonNegativeNumberSchema,
      netLabel: trimmedStringSchema.nullable(),
      photoUrl: trimmedStringSchema.nullable(),
      notes: trimmedStringSchema.nullable(),
      capturedAt: IsoDateTimeSchema,
      reviewStatus: ShipmentWeighReviewStatusSchema,
      stateLabel: trimmedStringSchema,
      entryStatus: ShipmentWeighEntryStatusSchema,
      entryStatusLabel: trimmedStringSchema,
      inventoryComparisonStatus: ShipmentWeighInventoryComparisonStatusSchema,
      inventoryLabel: trimmedStringSchema,
      paymentThresholdStatus: ShipmentWeighPaymentThresholdStatusSchema,
      paymentThresholdLabel: trimmedStringSchema,
      recovery: ShipmentWeighEventRecoverySchema.nullable().optional(),
    })
    .strict();

export const ShipmentQuickCaptureMaterialTaskMetadataSchema: z.ZodType<ShipmentQuickCaptureMaterialTaskMetadataDto> =
  z
    .object({
      taskStatus: ShipmentQuickCaptureMaterialTaskStatusSchema,
      taskStatusLabel: trimmedStringSchema,
      canSubmit: z.boolean(),
      cannotSubmitReason: ShipmentQuickCaptureCannotSubmitReasonSchema.nullable(),
    })
    .strict();

export const ShipmentQuickCaptureReadinessSummarySchema: z.ZodType<ShipmentQuickCaptureReadinessSummaryDto> =
  z
    .object({
      status: ShipmentQuickCaptureReadinessStatusSchema,
      label: trimmedStringSchema,
      materialCount: z.number().int().min(0),
      activeMaterialCount: z.number().int().min(0),
      completedMaterialCount: z.number().int().min(0),
      unavailableMaterialCount: z.number().int().min(0),
      needsCorrectionCount: z.number().int().min(0),
      needsCaptureCount: z.number().int().min(0),
      needsSecondWeightCount: z.number().int().min(0),
    })
    .strict();

export const ShipmentQuickCaptureMaterialRefSchema = z
  .object({
    shipmentMaterialRef: ShipmentMaterialRefSchema,
    materialId: z.uuid(),
    materialLabel: trimmedStringSchema.nullable(),
    huExpectedPhysicalWeightLb: nullableNonNegativeNumberSchema,
    reportableWeightLb: nullableNonNegativeNumberSchema,
    minimumPaymentThresholdLb: nullableNonNegativeNumberSchema,
    evidenceSummary: DriverShipmentWeighEvidenceSummarySchema,
    comparison: DriverShipmentWeighComparisonSummarySchema,
    netFeedback: DriverShipmentWeighNetFeedbackSchema,
    weighCards: z.array(ShipmentWeighOperationalCardSchema),
    recentEvents: z.array(DriverShipmentWeighEventSummarySchema),
    task: ShipmentQuickCaptureMaterialTaskMetadataSchema,
  })
  .strict();

export const ShipmentQuickCaptureTargetSchema = z
  .object({
    targetKind: ShipmentQuickCaptureTargetKindSchema,
    capturePhase: ShipmentQuickCaptureCapturePhaseSchema,
    shipmentId: z.uuid(),
    outboundRecordType: OutboundRecordTypeSchema,
    outboundRecordId: z.uuid(),
    shipmentReference: trimmedStringSchema,
    receiverLabel: trimmedStringSchema.nullable(),
    departedAt: IsoDateTimeSchema.nullable(),
    allowedScaleRoles: z.array(ShipmentWeighScaleRoleSchema).nonempty(),
    materialRefs: z.array(ShipmentQuickCaptureMaterialRefSchema).nonempty(),
    readinessSummary: ShipmentQuickCaptureReadinessSummarySchema,
    recommendedNextMaterialRefId: z.uuid().nullable(),
  })
  .strict();

export const ListShipmentQuickCaptureTargetsRequestSchema: z.ZodType<ListShipmentQuickCaptureTargetsRequest> =
  z
    .object({
      companyId: z.uuid(),
    })
    .strict();

export const ListShipmentQuickCaptureTargetsResponseSchema: z.ZodType<ListShipmentQuickCaptureTargetsResponse> =
  z
    .object({
      targets: z.array(ShipmentQuickCaptureTargetSchema),
    })
    .strict();

export const GetAssignedShipmentQuickCaptureAvailabilityRequestSchema: z.ZodType<GetAssignedShipmentQuickCaptureAvailabilityRequest> =
  z
    .object({
      companyId: z.uuid(),
    })
    .strict();

export const GetAssignedShipmentQuickCaptureAvailabilityResponseSchema: z.ZodType<GetAssignedShipmentQuickCaptureAvailabilityResponse> =
  z
    .object({
      hasActiveAssignedCaptureTargets: z.boolean(),
    })
    .strict();

export const GetShipmentQuickCaptureTargetRequestSchema: z.ZodType<GetShipmentQuickCaptureTargetRequest> =
  z
    .object({
      companyId: z.uuid(),
      shipmentId: z.uuid(),
    })
    .strict();

export const GetShipmentQuickCaptureTargetResponseSchema: z.ZodType<GetShipmentQuickCaptureTargetResponse> =
  z
    .object({
      target: ShipmentQuickCaptureTargetSchema,
    })
    .strict();

export const CreateShipmentQuickCaptureEventRequestSchema: z.ZodType<CreateShipmentQuickCaptureEventRequest> =
  z
    .object({
      companyId: z.uuid(),
      ...outboundWeighEvidenceReferenceSchema,
      shipmentMaterialRef: ShipmentMaterialRefSchema,
      continuationOfEventId: z.uuid().nullable().optional(),
      scaleRole: ShipmentWeighScaleRoleSchema,
      scaleLabel: noteSchema,
      weighType: ShipmentWeighTypeSchema,
      weightLb: optionalNullablePositiveNumberSchema,
      grossWeightLb: optionalNullablePositiveNumberSchema,
      tareWeightLb: optionalNullablePositiveNumberSchema,
      photoUrl: noteSchema,
      notes: noteSchema,
    })
    .strict()
    .superRefine(validateOutboundWeighEvidenceReference)
    .superRefine(validateSubmittedWeighValues);

export const CreateShipmentQuickCaptureEventResponseSchema: z.ZodType<CreateShipmentQuickCaptureEventResponse> =
  z
    .object({
      target: ShipmentQuickCaptureTargetSchema,
      event: DriverShipmentWeighEventSummarySchema,
    })
    .strict();

export const RecoverShipmentQuickCaptureEventRequestSchema: z.ZodType<RecoverShipmentQuickCaptureEventRequest> =
  z
    .object({
      companyId: z.uuid(),
      ...outboundWeighEvidenceReferenceSchema,
      weighEventId: z.uuid(),
      action: ShipmentWeighEventRecoveryActionSchema,
      scaleRole: ShipmentWeighScaleRoleSchema.nullable().optional(),
      scaleLabel: noteSchema,
      weighType: ShipmentWeighTypeSchema.nullable().optional(),
      weightLb: optionalNullablePositiveNumberSchema,
      grossWeightLb: optionalNullablePositiveNumberSchema,
      tareWeightLb: optionalNullablePositiveNumberSchema,
      photoUrl: noteSchema,
      notes: noteSchema,
    })
    .strict()
    .superRefine(validateOutboundWeighEvidenceReference)
    .superRefine(validateRecoveryReplacementValues);

export const RecoverShipmentQuickCaptureEventResponseSchema: z.ZodType<RecoverShipmentQuickCaptureEventResponse> =
  z
    .object({
      target: ShipmentQuickCaptureTargetSchema,
      event: DriverShipmentWeighEventSummarySchema,
    })
    .strict();

export const DriverShipmentWeighCaptureItemSchema: z.ZodType<DriverShipmentWeighCaptureItemDto> = z
  .object({
    shipmentId: z.uuid(),
    shipmentReference: trimmedStringSchema,
    shipmentMaterialRef: ShipmentMaterialRefSchema,
    materialId: z.uuid(),
    materialLabel: trimmedStringSchema.nullable(),
    receiverLabel: trimmedStringSchema.nullable(),
    departedAt: IsoDateTimeSchema.nullable(),
    huExpectedPhysicalWeightLb: nullableNonNegativeNumberSchema,
    reportableWeightLb: nullableNonNegativeNumberSchema,
    minimumPaymentThresholdLb: nullableNonNegativeNumberSchema,
    evidenceSummary: DriverShipmentWeighEvidenceSummarySchema,
    comparison: DriverShipmentWeighComparisonSummarySchema,
    netFeedback: DriverShipmentWeighNetFeedbackSchema,
    officialSettlement: DriverShipmentOfficialSettlementSummarySchema.nullable(),
    weighCards: z.array(ShipmentWeighOperationalCardSchema),
    recentEvents: z.array(DriverShipmentWeighEventSummarySchema),
  })
  .strict();

export const ListDriverShipmentWeighCaptureRequestSchema: z.ZodType<ListDriverShipmentWeighCaptureRequest> =
  z
    .object({
      companyId: z.uuid(),
    })
    .strict();

export const ListDriverShipmentWeighCaptureResponseSchema: z.ZodType<ListDriverShipmentWeighCaptureResponse> =
  z
    .object({
      items: z.array(DriverShipmentWeighCaptureItemSchema),
    })
    .strict();

export const GetDriverShipmentWeighCaptureRequestSchema: z.ZodType<GetDriverShipmentWeighCaptureRequest> =
  z
    .object({
      companyId: z.uuid(),
      shipmentId: z.uuid(),
    })
    .strict();

export const GetDriverShipmentWeighCaptureResponseSchema: z.ZodType<GetDriverShipmentWeighCaptureResponse> =
  z
    .object({
      item: DriverShipmentWeighCaptureItemSchema,
    })
    .strict();

export const CreateDriverShipmentWeighEventRequestSchema: z.ZodType<CreateDriverShipmentWeighEventRequest> =
  z
    .object({
      companyId: z.uuid(),
      shipmentId: z.uuid(),
      shipmentMaterialRef: ShipmentMaterialRefSchema.optional(),
      weighType: ShipmentWeighTypeSchema,
      weightLb: optionalNullablePositiveNumberSchema,
      grossWeightLb: optionalNullablePositiveNumberSchema,
      tareWeightLb: optionalNullablePositiveNumberSchema,
      photoUrl: noteSchema,
      notes: noteSchema,
    })
    .strict()
    .superRefine(validateSubmittedWeighValues);

export const CreateDriverShipmentWeighEventResponseSchema: z.ZodType<CreateDriverShipmentWeighEventResponse> =
  z
    .object({
      item: DriverShipmentWeighCaptureItemSchema,
      event: DriverShipmentWeighEventSummarySchema,
    })
    .strict();

export const ShipmentDriverAssignmentSchema: z.ZodType<ShipmentDriverAssignmentDto> = z
  .object({
    assignmentId: z.uuid(),
    companyId: z.uuid(),
    shipmentId: z.uuid(),
    outboundRecordType: OutboundRecordTypeSchema,
    outboundRecordId: z.uuid(),
    employeeId: z.uuid(),
    employeeDisplayName: trimmedStringSchema.nullable(),
    employeeNumber: trimmedStringSchema.nullable(),
    assignedByUserId: z.uuid().nullable(),
    assignedAt: IsoDateTimeSchema,
    status: ShipmentDriverAssignmentStatusSchema,
    removedByUserId: z.uuid().nullable(),
    removedAt: IsoDateTimeSchema.nullable(),
    removalReason: trimmedStringSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const ShipmentDriverAssignmentEmployeeOptionSchema: z.ZodType<ShipmentDriverAssignmentEmployeeOptionDto> =
  z
    .object({
      employeeId: z.uuid(),
      displayName: trimmedStringSchema,
      employeeNumber: trimmedStringSchema.nullable(),
    })
    .strict();

export const ListShipmentDriverAssignmentsRequestSchema: z.ZodType<ListShipmentDriverAssignmentsRequest> =
  z
    .object({
      companyId: z.uuid(),
      ...outboundWeighEvidenceReferenceSchema,
      includeRemoved: z.boolean().optional(),
    })
    .strict()
    .superRefine(validateOutboundWeighEvidenceReference);

export const ListShipmentDriverAssignmentsResponseSchema: z.ZodType<ListShipmentDriverAssignmentsResponse> =
  z
    .object({
      assignments: z.array(ShipmentDriverAssignmentSchema),
      availableDrivers: z.array(ShipmentDriverAssignmentEmployeeOptionSchema),
    })
    .strict();

export const AssignShipmentDriverRequestSchema: z.ZodType<AssignShipmentDriverRequest> = z
  .object({
    companyId: z.uuid(),
    ...outboundWeighEvidenceReferenceSchema,
    employeeId: z.uuid(),
  })
  .strict()
  .superRefine(validateOutboundWeighEvidenceReference);

export const AssignShipmentDriverResponseSchema: z.ZodType<AssignShipmentDriverResponse> = z
  .object({
    assignment: ShipmentDriverAssignmentSchema,
  })
  .strict();

export const RemoveShipmentDriverAssignmentRequestSchema: z.ZodType<RemoveShipmentDriverAssignmentRequest> =
  z
    .object({
      companyId: z.uuid(),
      ...outboundWeighEvidenceReferenceSchema,
      assignmentId: z.uuid(),
      reason: trimmedStringSchema,
    })
    .strict()
    .superRefine(validateOutboundWeighEvidenceReference);

export const RemoveShipmentDriverAssignmentResponseSchema: z.ZodType<RemoveShipmentDriverAssignmentResponse> =
  z
    .object({
      assignment: ShipmentDriverAssignmentSchema,
    })
    .strict();

export const ListShipmentWeighEvidenceRequestSchema: z.ZodType<ListShipmentWeighEvidenceRequest> = z
  .object({
    companyId: z.uuid(),
    ...outboundWeighEvidenceReferenceSchema,
    stationId: z.uuid(),
  })
  .strict()
  .superRefine(validateOutboundWeighEvidenceReference);

export const ListShipmentWeighEvidenceResponseSchema: z.ZodType<ListShipmentWeighEvidenceResponse> =
  z
    .object({
      materialRefs: z.array(ShipmentMaterialRefSchema),
      events: z.array(ShipmentWeighEventSchema),
      pairSuggestions: z.array(ShipmentWeighPairSuggestionSchema),
      netCandidates: z.array(ShipmentWeighNetCandidateSchema),
      operationalCards: z.array(ShipmentWeighOperationalCardSchema),
      comparison: ShipmentWeighEvidenceComparisonSchema,
    })
    .strict();

export const CreateShipmentWeighEventRequestSchema: z.ZodType<CreateShipmentWeighEventRequest> = z
  .object({
    companyId: z.uuid(),
    ...outboundWeighEvidenceReferenceSchema,
    stationId: z.uuid(),
    shipmentMaterialRef: ShipmentMaterialRefSchema.optional(),
    scaleRole: ShipmentWeighScaleRoleSchema,
    scaleLabel: noteSchema,
    weighType: ShipmentWeighTypeSchema,
    weightLb: optionalNullablePositiveNumberSchema,
    grossWeightLb: optionalNullablePositiveNumberSchema,
    tareWeightLb: optionalNullablePositiveNumberSchema,
    photoUrl: noteSchema,
    notes: noteSchema,
    captureSource: ShipmentWeighCaptureSourceSchema,
    capturedAt: IsoDateTimeSchema,
  })
  .strict()
  .superRefine(validateOutboundWeighEvidenceReference)
  .superRefine(validateSubmittedWeighValues);

export const CreateShipmentWeighEventResponseSchema: z.ZodType<CreateShipmentWeighEventResponse> = z
  .object({
    event: ShipmentWeighEventSchema,
  })
  .strict();

export const LinkShipmentWeighEventsRequestSchema: z.ZodType<LinkShipmentWeighEventsRequest> = z
  .object({
    companyId: z.uuid(),
    ...outboundWeighEvidenceReferenceSchema,
    stationId: z.uuid(),
    tareEventId: z.uuid(),
    grossEventId: z.uuid(),
    note: noteSchema,
  })
  .strict()
  .superRefine(validateOutboundWeighEvidenceReference);

export const LinkShipmentWeighEventsResponseSchema: z.ZodType<LinkShipmentWeighEventsResponse> = z
  .object({
    event: ShipmentWeighEventSchema,
  })
  .strict();

export const UnlinkShipmentWeighEventRequestSchema: z.ZodType<UnlinkShipmentWeighEventRequest> = z
  .object({
    companyId: z.uuid(),
    ...outboundWeighEvidenceReferenceSchema,
    stationId: z.uuid(),
    tareEventId: z.uuid(),
  })
  .strict()
  .superRefine(validateOutboundWeighEvidenceReference);

export const UnlinkShipmentWeighEventResponseSchema: z.ZodType<UnlinkShipmentWeighEventResponse> = z
  .object({
    event: ShipmentWeighEventSchema,
  })
  .strict();

export const VerifyShipmentWeighEventRequestSchema: z.ZodType<VerifyShipmentWeighEventRequest> = z
  .object({
    companyId: z.uuid(),
    ...outboundWeighEvidenceReferenceSchema,
    stationId: z.uuid(),
    weighEventId: z.uuid(),
  })
  .strict()
  .superRefine(validateOutboundWeighEvidenceReference);

export const VerifyShipmentWeighEventResponseSchema: z.ZodType<VerifyShipmentWeighEventResponse> = z
  .object({
    event: ShipmentWeighEventSchema,
  })
  .strict();

export const CorrectShipmentWeighEventRequestSchema: z.ZodType<CorrectShipmentWeighEventRequest> = z
  .object({
    companyId: z.uuid(),
    ...outboundWeighEvidenceReferenceSchema,
    stationId: z.uuid(),
    weighEventId: z.uuid(),
    correctedWeightLb: optionalNullablePositiveNumberSchema,
    correctedGrossWeightLb: optionalNullablePositiveNumberSchema,
    correctedTareWeightLb: optionalNullablePositiveNumberSchema,
    reason: trimmedStringSchema,
  })
  .strict()
  .superRefine(validateOutboundWeighEvidenceReference);

export const CorrectShipmentWeighEventResponseSchema: z.ZodType<CorrectShipmentWeighEventResponse> =
  z
    .object({
      event: ShipmentWeighEventSchema,
    })
    .strict();

export const VoidShipmentWeighEventRequestSchema: z.ZodType<VoidShipmentWeighEventRequest> = z
  .object({
    companyId: z.uuid(),
    ...outboundWeighEvidenceReferenceSchema,
    stationId: z.uuid(),
    weighEventId: z.uuid(),
    reason: trimmedStringSchema,
  })
  .strict()
  .superRefine(validateOutboundWeighEvidenceReference);

export const VoidShipmentWeighEventResponseSchema: z.ZodType<VoidShipmentWeighEventResponse> = z
  .object({
    event: ShipmentWeighEventSchema,
  })
  .strict();

type SubmittedWeighValueInput = Pick<
  | CreateShipmentWeighEventRequest
  | CreateDriverShipmentWeighEventRequest
  | CreateShipmentQuickCaptureEventRequest,
  'weighType' | 'weightLb' | 'grossWeightLb' | 'tareWeightLb'
>;

type RecoveryReplacementInput = Pick<
  RecoverShipmentQuickCaptureEventRequest,
  'action' | 'scaleRole' | 'weighType' | 'weightLb' | 'grossWeightLb' | 'tareWeightLb'
>;

function validateRecoveryReplacementValues(
  value: RecoveryReplacementInput,
  ctx: z.RefinementCtx,
): void {
  if (value.action === 'WITHDRAW') {
    return;
  }

  if (!value.scaleRole) {
    ctx.addIssue({
      code: 'custom',
      message: 'scaleRole is required when replacing a shipment weigh event.',
      path: ['scaleRole'],
    });
  }
  if (!value.weighType) {
    ctx.addIssue({
      code: 'custom',
      message: 'weighType is required when replacing a shipment weigh event.',
      path: ['weighType'],
    });
    return;
  }

  validateSubmittedWeighValues(
    {
      weighType: value.weighType,
      weightLb: value.weightLb,
      grossWeightLb: value.grossWeightLb,
      tareWeightLb: value.tareWeightLb,
    },
    ctx,
  );
}

function validateSubmittedWeighValues(value: SubmittedWeighValueInput, ctx: z.RefinementCtx): void {
  if (value.weighType === 'MANUAL_PAIR') {
    if (typeof value.grossWeightLb !== 'number') {
      ctx.addIssue({
        code: 'custom',
        message: 'grossWeightLb is required for MANUAL_PAIR.',
        path: ['grossWeightLb'],
      });
    }
    if (typeof value.tareWeightLb !== 'number') {
      ctx.addIssue({
        code: 'custom',
        message: 'tareWeightLb is required for MANUAL_PAIR.',
        path: ['tareWeightLb'],
      });
    }
    if (
      typeof value.grossWeightLb === 'number' &&
      typeof value.tareWeightLb === 'number' &&
      value.grossWeightLb < value.tareWeightLb
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'grossWeightLb must be greater than or equal to tareWeightLb.',
        path: ['grossWeightLb'],
      });
    }
    return;
  }

  if (typeof value.weightLb !== 'number') {
    ctx.addIssue({
      code: 'custom',
      message: 'weightLb is required for GROSS, TARE, and NET_DIRECT.',
      path: ['weightLb'],
    });
  }
}
